import React, { useCallback, useContext, useMemo, useState, useRef, useEffect } from "react";
import { Modal, Button, Row, Col, InputNumber } from "antd";
// API
import { onUpdateOptionButton } from "api/teamRunApi";
// Helpers
import { toast } from "helpers/generalHelper";
// Style
import "./UpdateOption.scss";
import { ACTION_BUTTONS_NAME } from "constants/generalActionButtons";
import { GlobalContext } from "contexts/GlobalContext";
import CompanyTraders from "components/EditTradeDetails/CompanysTrader";
import { EDIT_TRADE_DETAILS_LABELS } from "constants/auditTrail";
import AddCompanyForm from "../AddCompanyForm";
import AgreedDeltaInputs from "../AgreedDeltaInputs";
import { AddButton } from "components/Common/CustomButtons/CustomButtons";
import useDraggableModal from "hooks/useDraggableModal";
import {
  agreedDeltasValidForSubmit,
  applyAgreedDeltaReadingAt,
  isLiveTeamRunOption,
  setAgreedDeltaField,
  toggleAgreedDeltaUnitAt,
  withAgreedDeltasSubmitPayload,
} from "helpers/TeamRun/agreedDelta";

const BUTTONS_NAME = {
  UPDATE_BID: {
    label: "Update bid",
    key: "bid",
    color: "bid_button_color",
    show: true,
  },
  UPDATE_ASK: {
    label: "Update ask",
    key: "ask",
    color: "ask_button_color",
    show: true,
  },

  UPDATE_MARKET: {
    label: "Update market",
    key: "market",
    color: "market_button_color",
    show: true,
  },
};

const AlignedLabelAndData = (props) => {
  return (
    <div className="align-label-data__container">
      <h6 className={props.theme ? "label-light" : "label-dark"}>
        {props.label}
      </h6>
      <span dangerouslySetInnerHTML={{ __html: props.value }} />
    </div>
  );
};

const UpdateOption = (props) => {
  const { summaryId, onClose, trader, quantity: propsQuantity, colors, data, productId, isOpen, agreedDeltas: propsAgreedDeltas } = props;
  const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);
  const [refetchCompanies, setRefetchCompanies] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentButton, setCurrentButton] = useState();
  const [tradingCompanies, setTradingCompanies] = useState(trader);
  const [quantity, setQuantity] = useState({
    bid_quantity: propsQuantity.bid_quantity,
    ask_quantity: propsQuantity.ask_quantity,
  });
  const [agreedDeltas, setAgreedDeltas] = useState(propsAgreedDeltas ?? []);
  const debounceTimerRef = useRef(null);
  
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);
  const { modalRender, title: draggableTitle } = useDraggableModal(
    isOpen,
    ACTION_BUTTONS_NAME.UPDATE_OPTION.label
  );

  // Update quantity state when props.quantity changes
  useEffect(() => {
    setQuantity({
      bid_quantity: propsQuantity?.bid_quantity,
      ask_quantity: propsQuantity?.ask_quantity,
    });
  }, [propsQuantity]);

  useEffect(() => {
    setAgreedDeltas(propsAgreedDeltas ?? []);
  }, [propsAgreedDeltas]);

  const handleAgreedDeltaChange = (index, field, value) => {
    setAgreedDeltas((prev) => setAgreedDeltaField(prev, index, field, value));
  };

  const handleAgreedDeltaUnitToggle = (index) => {
    setAgreedDeltas((prev) => toggleAgreedDeltaUnitAt(prev, index));
  };

  const handleAgreedDeltaSuggestion = (index, reading) => {
    setAgreedDeltas((prev) => applyAgreedDeltaReadingAt(prev, index, reading));
  };

  const optionLive = colors?.live;
  const showAgreedDeltas = !isLiveTeamRunOption(optionLive);
  const deltasValid = agreedDeltasValidForSubmit(optionLive, agreedDeltas);

  const sideAccentStyle = (itemKey) => {
    const color = itemKey?.startsWith("bid")
      ? colors?.bid_button_color
      : colors?.ask_button_color;
    return color
      ? { borderLeft: `3px solid ${color}`, paddingLeft: 8 }
      : undefined;
  };

  const onFooterClick = useCallback(
    (keyButton) => {
      console.log(keyButton);
      setCurrentButton(keyButton);
      if (
        tradingCompanies
          .map((item) => item.key?.startsWith(keyButton) && item.value)
          .includes(null)
      )
        return toast("Please add a valid trader name", "warning");

      if (!deltasValid) return;

      const extractTradingCompanies = {
        ask_trading_company_id: tradingCompanies.find(
          (item) => item.key === "ask_trading_company_id"
        )?.value,
        bid_trading_company_id: tradingCompanies.find(
          (item) => item.key === "bid_trading_company_id"
        )?.value,
      };
      //remove null value
      Object.keys(extractTradingCompanies).forEach((key) => {
        if (extractTradingCompanies[key] === null) {
          delete extractTradingCompanies[key];
        }
      });
      const prepareAPIData = withAgreedDeltasSubmitPayload(
        {
          summary_id: summaryId,
          type: keyButton,
          trading_companies: extractTradingCompanies,
          bid_quantity: quantity.bid_quantity,
          ask_quantity: quantity.ask_quantity,
        },
        optionLive,
        agreedDeltas
      );

      setIsLoading(true);
      onUpdateOptionButton(prepareAPIData)
        .then((res) => {
          toast(res?.data?.message, "success");
          setIsLoading(false);
          onClose(false);
        })
        .catch((err) => {
          setIsLoading(false);
          console.log(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      tradingCompanies,
      quantity,
      summaryId,
      onClose,
      agreedDeltas,
      deltasValid,
      optionLive,
    ]
  );

  const customizedFooter = useMemo(() => {
    return Object.values(BUTTONS_NAME).map((item) => {
      if (item.key === "ask") item.show = colors?.show_ask_button;

      if (item.key === "bid") item.show = colors?.show_bid_button;

      if (item.key === "market") item.show = colors?.show_market_button;

      return (
        item.show && (
          <Button
            loading={item.key === currentButton && isLoading}
            disabled={!deltasValid}
            key={item.key}
            onClick={() => onFooterClick(item.key)}
            className="footer"
            style={
              deltasValid
                ? {
                    border: `1px solid ${colors?.[item.color]}`,
                    color: colors?.[item.color],
                  }
                : undefined
            }
          >
            {item.label}
          </Button>
        )
      );
    });
  }, [onFooterClick, isLoading, currentButton, colors, deltasValid]);

  const handleTraderChange = (index, e) => {
    const updatedTraders = [...tradingCompanies];
    updatedTraders[index].value = e;
    setTradingCompanies(updatedTraders);
  };

  // Extract quantity key from item.key (e.g., "bid_quantity" from "bid_trading_company_id")
  const getQuantityKey = (itemKey) => {
    if (itemKey?.startsWith("bid")) return "bid_quantity";
    if (itemKey?.startsWith("ask")) return "ask_quantity";
    return null;
  };

  // Debounced quantity update handler
  const handleQuantityChange = useCallback((itemKey, value) => {
    const quantityKey = getQuantityKey(itemKey);
    if (!quantityKey) return;

    // Update quantity state immediately for UI responsiveness
    setQuantity((prevQuantity) => ({
      ...prevQuantity,
      [quantityKey]: value,
    }));

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer (500ms delay)
    debounceTimerRef.current = setTimeout(() => {
      // Debounced update - you can add any side effects here if needed
      // For now, the state is already updated above
    }, 500);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  function onAddCompany() {
    setIsAddCompanyOpen(true);
  }

  return (
    <>
      <Modal
        getContainer={"#content-root"}
        width={"55vw"}
        open={isOpen}
        title={draggableTitle}
        modalRender={modalRender}
        onCancel={() => onClose(false)}
        footer={[
          <AddButton
            key={EDIT_TRADE_DETAILS_LABELS.TRADING_COMPANY}
            onClick={() => {
              onAddCompany();
            }}
          >
            {EDIT_TRADE_DETAILS_LABELS.TRADING_COMPANY}
          </AddButton>,
          ...customizedFooter,
        ]}
        className={`update-option__container ${dark_Theme && "ant-modal-dark"}`}
      >
        {data.map((item) => {
          return (
            <React.Fragment key={`${item.value}-${item.label}`}>
              <AlignedLabelAndData
                label={item.label}
                value={item.value}
                theme={dark_Theme}
              />
            </React.Fragment>
          );
        })}
        {tradingCompanies.map((item, index) => (
          <Row
            key={index}
            className="trader-input__container"
            style={sideAccentStyle(item.key)}
          >
            <Col span={8} md={5} lg={8}>
              <h6 className={dark_Theme ? "label-light" : "label-dark"}>
                {item.label}
              </h6>
            </Col>
            <Col span={5}>
              <InputNumber
                value={quantity[getQuantityKey(item.key)] ?? propsQuantity?.[getQuantityKey(item.key)]}
                onChange={(value) => handleQuantityChange(item.key, value)}
                min={0}
                step={1}
                precision={0}
                addonAfter={"kt"}
              />
            </Col>
            <Col span={10} xs={4} lg={10}>
              <CompanyTraders
                defaultValue={item.value}
                onChange={(e) => handleTraderChange(index, e)}
                placeholder="Select Trader Company"
                isPopup={true}
                value={item?.value || ""}
                refetchCompanies={refetchCompanies}
                setRefetchCompanies={setRefetchCompanies}
                productId={productId}
              />
            </Col>
          </Row>
        ))}
        {showAgreedDeltas ? (
          <AgreedDeltaInputs
            groups={agreedDeltas}
            onChange={handleAgreedDeltaChange}
            onToggleUnit={handleAgreedDeltaUnitToggle}
            onApplySuggestion={handleAgreedDeltaSuggestion}
            darkTheme={dark_Theme}
            disabled={isLoading}
          />
        ) : null}
      </Modal>
      {isAddCompanyOpen && (
        <AddCompanyForm
          setRefetchCompanies={setRefetchCompanies}
          isOpen={isAddCompanyOpen}
          setIsOpen={setIsAddCompanyOpen}
          productId={productId}
        />
      )}
    </>
  );
};

export default UpdateOption;
